import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormActions,
    FormFields,
    FormTextField,
    FormMultiselectDropdown,
    FormDropdown,
    mapToDropdownOption,
    NamedProperty,
    ValidationResult,
    FormValidationSummary
} from "sprint-common-web";

import { FormDistributorSearchDropdown } from "../../../../common/components/form/FormDistributorDropdown";
import { VersionType } from "../../../../common/data/lookup-data-providers/VersionTypesDataProvider";
import { Airline } from "../../../../common/types/Airline";
import { EditAncillaryFormInputs } from "./EditAncillaryFormInputs";
import { useEditAncillaryForm } from "./useEditAncillaryForm";

export interface EditAncillaryFormProps {
    onCancel(): void;
    onSubmit(editedAncillary: UpdatedAncillaryItem): void;
    ancillaryToEditId: string;
}

export interface UpdatedAncillaryItem {
    id: string;
    data: {
        name: string;
        versionType: VersionType;
        airlines: Airline[];
        distributor: NamedProperty;
    };
}

export const EditAncillaryForm: React.FC<EditAncillaryFormProps> = (props: EditAncillaryFormProps) => {
    const {
        defaultOptions,
        defaultValues,
        airlines,
        versionTypes,
        validateEditAncillary,
        editAncillary
    } = useEditAncillaryForm(props.ancillaryToEditId);

    const [validationResult, setValidationResult] = useState<ValidationResult>();

    const { errors, control, formState, reset, handleSubmit } = useForm<EditAncillaryFormInputs>({
        defaultValues,
        criteriaMode: "all"
    });

    const editCurrentAncillary = async (inputs: EditAncillaryFormInputs) => {
        setValidationResult(undefined);

        const result = await validateEditAncillary(inputs);
        setValidationResult(result);

        if (!result.isValid) {
            return;
        }

        const editedItem = await editAncillary(inputs);
        props.onSubmit(editedItem);
    };

    const handleCancelButtonClick = () => {
        reset();
        props.onCancel();
    };

    return (
        <Form onSubmit={handleSubmit(editCurrentAncillary)}>
            <FormFields>
                <FormTextField
                    label="Title"
                    control={control}
                    name="title"
                    errors={errors}
                    rules={{ required: "Enter correct title" }}
                />
                <FormMultiselectDropdown
                    options={airlines.map((item) => mapToDropdownOption(item))}
                    control={control}
                    errors={errors}
                    rules={{ required: "Search and select airlines" }}
                    name="airlineIds"
                    label="Airlines"
                />
                <FormDropdown
                    options={versionTypes.map((item) => mapToDropdownOption(item))}
                    control={control}
                    errors={errors}
                    rules={{ required: "Search and select version type" }}
                    name="versionTypeId"
                    label="Version Type"
                />
                <FormDistributorSearchDropdown
                    initOptions={defaultOptions.distributors}
                    control={control}
                    errors={errors}
                    rules={{ required: "Search and select distributor" }}
                    name="distributorId"
                    label="Distributor"
                    placeholder={"Distributor"}
                />
            </FormFields>
            {validationResult && <FormValidationSummary validationResult={validationResult} />}
            <FormActions
                isLoading={formState.isValidating || formState.isSubmitting}
                onCancel={handleCancelButtonClick}
            />
        </Form>
    );
};
